// src/components/sidebar/ConversationItem.tsx
import React, { useState } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { MoreVert, Edit, Delete, Share, MapOutlined, CheckCircleOutlined } from "@mui/icons-material";
import {
  Conversation,
  useConversationStore,
} from "../../stores/conversationStore";
import { useT } from '../../i18n/useT';

interface ConversationItemProps {
  conversation: Conversation;
  onClick: () => void;
  isActive?: boolean;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  onClick,
  isActive = false,
}) => {
  const t = useT();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title);

  const { renameConversation, deleteConversation } = useConversationStore();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRename = async () => {
    if (newTitle.trim() && newTitle.trim() !== conversation.title) {
      await renameConversation(conversation.id, newTitle.trim());
    }
    setShowRenameDialog(false);
    handleMenuClose();
  };

  const handleDelete = async () => {
    await deleteConversation(conversation.id);
    setShowDeleteDialog(false);
    handleMenuClose();
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const truncateTitle = (title: string, maxLength: number = 50) => {
    return title.length > maxLength
      ? `${title.substring(0, maxLength)}...`
      : title;
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={onClick}
          selected={isActive}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            "&.Mui-selected": {
              backgroundColor: "action.selected",
            },
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ListItemText
            primary={
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                {/* Inline map-pin badge marks tour conversations so
                    they're recognizable at a glance in the list. */}
                {conversation.type === 'walking_tour' && (
                  <MapOutlined
                    sx={{
                      fontSize: 16,
                      color: conversation.walking_tour?.completed_at
                        ? 'success.main'
                        : 'warning.main',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: isActive ? 600 : 400, minWidth: 0 }}
                >
                  {truncateTitle(conversation.title)}
                </Typography>
              </Box>
            }
            secondary={
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  {formatDate(conversation.updated_at)}
                </Typography>
                {conversation.type === 'walking_tour' &&
                  conversation.walking_tour && (
                    <>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        •
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color={
                          conversation.walking_tour.completed_at
                            ? 'success.main'
                            : 'warning.main'
                        }
                        sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                      >
                        {conversation.walking_tour.completed_at ? (
                          <>
                            <CheckCircleOutlined sx={{ fontSize: 12 }} />
                            
                            {t("Complete")}
                          </>
                        ) : (
                          <>
                            
                            {t("Stop")} {' '}
                            {(conversation.walking_tour.current_waypoint_index ?? 0) + 1}
                            /
                            {conversation.walking_tour.waypoints?.length ?? 0}
                          </>
                        )}
                      </Typography>
                    </>
                  )}
                {conversation.type !== 'walking_tour' &&
                  conversation.message_count > 0 && (
                    <>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        •
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {conversation.message_count}  {t("messages")}
                      </Typography>
                    </>
                  )}
              </Box>
            }
          />
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              opacity: 0,
              ".MuiListItemButton-root:hover &": {
                opacity: 1,
              },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </ListItemButton>
      </ListItem>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setShowRenameDialog(true);
            setNewTitle(conversation.title);
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          
          {t("Rename")}
        </MenuItem>
        <MenuItem onClick={() => setShowDeleteDialog(true)}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          
          {t("Delete")}
        </MenuItem>
        <MenuItem onClick={handleMenuClose} disabled>
          <Share fontSize="small" sx={{ mr: 1 }} />
          
          {t("Share (Coming Soon)")}
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog
        open={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Rename Conversation")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t("Conversation Title")}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            margin="normal"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRenameDialog(false)}>{t("Cancel")}</Button>
          <Button onClick={handleRename} variant="contained">
            
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>{t("Delete Conversation")}</DialogTitle>
        <DialogContent>
          <Typography>
            
            {t("Are you sure you want to delete \"")}{conversation.title}{t("\"? This action cannot be undone.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>{t("Cancel")}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
